import {
  Button,
  Flex,
  NumberInput,
  SimpleGrid,
  TextInput,
} from "@mantine/core";
import { SelectItem } from "@mantine/core/lib/Select/types";
import { DatePickerInput } from "@mantine/dates";
import { useDebouncedState, useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { OperationResult } from "@urql/core";
import { DataTableSortStatus } from "mantine-datatable";
import { useCallback, useState } from "react";

import { useCompanyOptions } from "../../api/hooks/customer/useCompanyOptions.ts";
import { useCreateDeskCollection } from "../../api/hooks/deskCollection/deskCollection/useCreateDeskCollection.ts";
import { useDeskCollections } from "../../api/hooks/deskCollection/deskCollection/useDeskCollections.ts";
import Calendar from "../../components/Icons/Calendar.tsx";
import SvgPlus from "../../components/Icons/Plus.tsx";
import { Select } from "../../components/Select.tsx";
import Table from "../../components/Table/Table.tsx";
import {
  CreateDeskCollectionInput,
  DeskCollectionOrderBy,
  DeskCollectionTypeFilterOptions,
  Exact,
  OrderBy,
  SendDeskCollectionInput,
  SendDeskCollectionMutation,
  UpdateDeskCollectionInput,
  UpdateDeskCollectionMutation,
} from "../../graphql/generated.ts";
import { useMutationNotificationWrapper } from "../../hooks/useMutationNotificationWrapper.tsx";
import { DeskCollection } from "../../types/deskCollection/deskCollection.ts";
import { getCompanyOptions } from "../../utils/company.ts";
import { getStartOfDayString } from "../../utils/date.ts";
import { parseStringToNumber } from "../../utils/number.ts";
import { getPagesCount } from "../../utils/pagination.ts";
import { DeskCollectionModal } from "./DeskCollectionModal.tsx";
import { useDeskCollectionTableData } from "./hooks/useDeskCollectionTableData.tsx";
import { FilterValue } from "./types.ts";
import {
  getDeskCollectionApiFilter,
  getDeskCollectionApiSort,
} from "./utils.ts";

interface Props {
  templateOptions: SelectItem[];
  onInlineEdit: (
    id: number,
    value: UpdateDeskCollectionInput
  ) => Promise<
    OperationResult<
      UpdateDeskCollectionMutation,
      Exact<{
        id: number;
        input: UpdateDeskCollectionInput;
      }>
    >
  >;
  onSend: (
    id: number,
    message: string
  ) => Promise<
    OperationResult<
      SendDeskCollectionMutation,
      Exact<{
        id: number;
        input: SendDeskCollectionInput;
      }>
    >
  >;
}

const DeskCollectionAddInvoice = ({
  templateOptions,
  onInlineEdit,
  onSend,
}: Props) => {
  const [page, setPage] = useState<number>(1);
  const [orderBy, setOrderBy] = useState<DeskCollectionOrderBy>({
    createdAt: OrderBy.Desc,
  });
  const [filter, setFilter] = useState<FilterValue>({});
  const [invoiceNumber, setInvoiceNumber] = useDebouncedState("", 500);
  const apiFilter = getDeskCollectionApiFilter({ ...filter, invoiceNumber });

  const [{ data: customersResponse, fetching: fetchingCustomers }] =
    useCompanyOptions();
  const customers = customersResponse?.customers.data;

  const [
    {
      data: deskCollectionResponse,
      error: deskCollectionError,
      fetching: fetchingDeskCollection,
    },
    refresh,
  ] = useDeskCollections({
    filter: {
      ...apiFilter,
      type: { equals: DeskCollectionTypeFilterOptions.Manual },
    },
    orderBy,
    page,
  });
  const deskCollections = deskCollectionResponse?.deskCollections.data;
  const pageCount = getPagesCount(
    deskCollectionResponse?.deskCollections.total
  );

  const error = deskCollectionError;

  const [, createDeskCollection] = useMutationNotificationWrapper(
    useCreateDeskCollection(),
    {
      success: {
        message: "Desk Collection record has been successfully created",
      },
    }
  );

  const [
    createModalOpened,
    { open: createModalOpen, close: createModalClose },
  ] = useDisclosure(false);

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage]
  );

  const handleOnSortChange = useCallback(
    (sort: DataTableSortStatus) => {
      setOrderBy(getDeskCollectionApiSort(sort));
    },
    [setOrderBy]
  );

  const handleCreate = useCallback(
    (input: CreateDeskCollectionInput) => {
      createDeskCollection({ input }).then((data) => {
        if (!data.error) {
          refresh();
        }
      });
    },
    [createDeskCollection, refresh]
  );

  const handleUpdate = useCallback(
    (item: DeskCollection, input: UpdateDeskCollectionInput) => {
      onInlineEdit(item.id, input).then((data) => {
        if (!data.error) {
          refresh();
        }
      });
    },
    [onInlineEdit, refresh]
  );

  const handleSend = useCallback(
    async (id: number, message: string) => {
      await onSend(id, message);
    },
    [onSend]
  );

  const handleFilterChange = useCallback(
    (key: keyof FilterValue, value: number | string | Date | undefined) => {
      setFilter((prevFilter) => ({
        ...prevFilter,
        [key]: value,
      }));
    },
    [setFilter]
  );

  const [columns, rows] = useDeskCollectionTableData({
    data: deskCollections ?? [],
    templates: templateOptions,
    onInlineEdit: handleUpdate,
    onSend: handleSend,
  });

  if (error) {
    notifications.show({
      message: "Something went wrong while trying to fetch data.",
      variant: "error",
    });
  }

  return (
    <Flex direction="column" gap={20}>
      <Button
        uppercase
        variant="filledGolden"
        leftIcon={<SvgPlus />}
        onClick={createModalOpen}
      >
        Add
      </Button>
      <SimpleGrid
        cols={5}
        breakpoints={[
          { maxWidth: "sm", cols: 1 },
          { maxWidth: "lg", cols: 2 },
        ]}
        spacing={20}
        verticalSpacing={8}
      >
        <Select
          loading={fetchingCustomers}
          searchable
          clearable
          size="m"
          label="Customer Name"
          placeholder="Customer Name"
          data={getCompanyOptions(customers)}
          onChange={(value) => {
            handleFilterChange("customerId", value ? Number(value) : undefined);
          }}
        />
        <TextInput
          size="m"
          label="Invoice Number"
          placeholder="Invoice Number"
          onChange={(event) => {
            setInvoiceNumber(event.target.value);
          }}
        />
        <DatePickerInput
          clearable
          label="Invoice Due Date"
          placeholder="Invoice Due Date"
          icon={<Calendar />}
          firstDayOfWeek={0}
          size="m"
          valueFormat="YYYY MMM DD"
          popoverProps={{ withinPortal: true }}
          onChange={(value) => {
            handleFilterChange(
              "invoiceDueDate",
              value ? getStartOfDayString(value) : undefined
            );
          }}
        />
        <NumberInput
          label="Invoice Due Date - Today"
          placeholder="Invoice Due Date - Today"
          size="m"
          precision={0}
          parser={parseStringToNumber}
          hideControls
          value={filter.dpd}
          onChange={(value: number) => handleFilterChange("dpd", value)}
        />
      </SimpleGrid>
      <Table
        columns={columns}
        rows={rows}
        verticalAlignment="top"
        loading={fetchingDeskCollection}
        pagination={{
          pageCount: pageCount,
          page: page,
          onPageChange: handlePageChange,
        }}
        onSortChange={handleOnSortChange}
      />
      {createModalOpened && (
        <DeskCollectionModal
          templates={templateOptions}
          opened={true}
          onClose={createModalClose}
          onSubmit={handleCreate}
        />
      )}
    </Flex>
  );
};

export default DeskCollectionAddInvoice;
